<template>
  <div class="aroma-report-list">
    <header class="aroma-report-list__header">
      <div class="aroma-report-list__heading">
        <div class="aroma-report-list__eyebrow">
          <span class="aroma-report-list__eyebrow-icon" aria-hidden="true">
            <van-icon name="description" />
          </span>
          <span>睡眠健康报告</span>
        </div>
        <h1>睡眠管家报告</h1>
        <p v-if="organizationName">{{ organizationName }}</p>
      </div>
      <div class="aroma-report-list__device">
        <span class="aroma-report-list__device-icon" aria-hidden="true"></span>
        <div class="aroma-report-list__device-copy">
          <span>设备 SN</span>
          <strong>{{ snText }}</strong>
        </div>
      </div>
    </header>

    <div v-if="errorMessage" class="aroma-report-list__content">
      <van-empty :description="errorMessage" />
    </div>

    <div v-else class="aroma-report-list__content">
      <div class="aroma-report-list__section-title">报告列表</div>
      <van-pull-refresh v-model="refreshing" success-text="刷新成功" @refresh="onRefresh">
        <van-list
          v-model="loading"
          :finished="finished"
          :immediate-check="false"
          finished-text="没有更多了"
          @load="onLoad"
        >
          <div v-if="reportList.length > 0" class="aroma-report-list__cards">
            <article
              v-for="item in reportList"
              :key="item.id"
              class="aroma-report-list__card"
              role="button"
              tabindex="0"
              @click="onReportClick(item)"
              @keyup.enter="onReportClick(item)"
              @keyup.space.prevent="onReportClick(item)"
            >
              <div class="aroma-report-list__card-primary">
                <strong class="aroma-report-list__name">{{ reportName(item) }}</strong>
                <div
                  class="aroma-report-list__summary"
                  :class="`is-${reportStatus(item.sleepEfficiency)}`"
                >
                  <span>睡眠效率</span>
                  <strong>{{ reportEfficiency(item.sleepEfficiency) }}</strong>
                  <span class="aroma-report-list__quality">{{ reportQuality(item) }}</span>
                </div>
                <time :datetime="item.reportDate">{{ reportDate(item.reportDate) }}</time>
              </div>
              <div class="aroma-report-list__card-secondary">
                <span>{{ reportSn(item) }}</span>
              </div>
            </article>
          </div>
        </van-list>
        <van-empty v-if="!loading && !refreshing && reportList.length === 0" description="暂无报告" />
      </van-pull-refresh>
    </div>
    <aroma-tabbar />
  </div>
</template>

<script>
import API from '@/api'
import {
  formatReportDate,
  formatReportName,
  formatSleepEfficiency,
  sleepEfficiencyStatus
} from '@/utils/aroma-report-list'
import { clearSniSession, getSniSession, isValidSniSn, saveSniSession } from '@/utils/sni-session'
import AromaTabbar from '@/components/aroma-report/AromaTabbar.vue'

export default {
  name: 'AromaReportList',

  components: {
    AromaTabbar
  },

  data () {
    return {
      loading: false,
      refreshing: false,
      finished: false,
      pageNum: 1,
      pageSize: 20,
      reportList: [],
      organizationName: '',
      errorMessage: ''
    }
  },

  computed: {
    sn () {
      return this.$route.query.sn || ''
    },

    snText () {
      return this.sn || '未获取到设备 SN'
    },

    session () {
      return getSniSession(this.sn)
    },

    token () {
      return this.session?.token
    }
  },

  mounted () {
    this.initPage()
  },

  methods: {
    async initPage () {
      if (!isValidSniSn(this.sn)) {
        this.errorMessage = '设备 SN 必须为14位数字'
        return
      }
      if (!this.token) {
        this.navigateToLogin()
        return
      }
      try {
        const profile = await API.sniCurrent(this.token)
        this.organizationName = profile.organizationName || ''
        saveSniSession(this.sn, {
          ...this.session,
          profileCompleted: profile.profileCompleted
        })
        if (!profile.profileCompleted) {
          this.$router.replace({ name: 'AromaReportProfile', query: { sn: this.sn } })
          return
        }
        await this.onRefresh()
      } catch (error) {
        clearSniSession(this.sn)
        this.navigateToLogin()
      }
    },

    navigateToLogin () {
      this.$router.replace({ name: 'AromaReportLogin', query: { sn: this.sn } })
    },

    async onRefresh () {
      this.refreshing = true
      this.finished = false
      this.pageNum = 1
      await this.getReportList(true)
    },

    async onLoad () {
      if (this.refreshing || this.finished) {
        this.loading = false
        return
      }
      await this.getReportList(false)
    },

    async getReportList (isRefresh) {
      if (!this.token) {
        this.loading = false
        this.refreshing = false
        this.navigateToLogin()
        return
      }
      try {
        const data = await API.sniReportPageList({
          sn: this.sn,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        }, this.token)
        const list = data?.list || []
        this.reportList = isRefresh ? list : this.reportList.concat(list)
        this.finished = data?.boolLastPage === true ||
          list.length === 0 ||
          (data?.total !== undefined && this.reportList.length >= Number(data.total))
        this.pageNum = (data?.pageNum || this.pageNum) + 1
      } catch (error) {
        if (error?.code === '8' || error?.code === '11') {
          clearSniSession(this.sn)
          this.navigateToLogin()
        } else {
          this.$toast(error?.message || error)
        }
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },

    reportName (item) {
      return formatReportName(item)
    },

    reportDate (value) {
      return formatReportDate(value)
    },

    reportEfficiency (value) {
      return formatSleepEfficiency(value)
    },

    reportStatus (value) {
      return sleepEfficiencyStatus(value)
    },

    reportSn (item) {
      return item.sn || this.snText
    },

    reportQuality (item) {
      return item.sleepQuality || '--'
    },

    onReportClick (item) {
      this.$router.push({
        name: 'AromaReportDetail',
        query: {
          sn: this.sn,
          reportId: item.id
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.aroma-report-list {
  width: 100%;
  min-height: 100%;
  padding: 16px 12px calc(80px + env(safe-area-inset-bottom));
  overflow-x: hidden;
  background: #f5f6f8;
  box-sizing: border-box;

  &__header {
    position: relative;
    display: flex;
    min-width: 0;
    overflow: hidden;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 18px;
    padding: 18px;
    border: 1px solid rgba(89, 106, 253, 0.12);
    border-radius: 16px;
    background:
      radial-gradient(circle at 92% 4%, rgba(111, 126, 255, 0.18) 0, rgba(111, 126, 255, 0) 38%),
      linear-gradient(145deg, #ffffff 0%, #f6f8ff 100%);
    box-shadow: 0 8px 24px rgba(50, 64, 108, 0.08);
    box-sizing: border-box;

    &::after {
      position: absolute;
      top: -42px;
      right: -34px;
      width: 112px;
      height: 112px;
      border: 18px solid rgba(89, 106, 253, 0.05);
      border-radius: 50%;
      content: '';
      pointer-events: none;
    }
  }

  &__heading {
    position: relative;
    z-index: 1;
    min-width: 0;

    h1 {
      margin: 10px 0 0;
      color: #24304a;
      font-size: 23px;
      font-weight: 700;
      line-height: 32px;
      letter-spacing: 0.2px;
    }

    p {
      margin: 4px 0 0;
      overflow: hidden;
      color: #7d8491;
      font-size: 13px;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #596afd;
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.5px;
  }

  &__eyebrow-icon {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: #ffffff;
    font-size: 15px;
    background: linear-gradient(145deg, #6878ff, #5062f4);
    box-shadow: 0 4px 10px rgba(89, 106, 253, 0.22);
  }

  &__device {
    position: relative;
    z-index: 1;
    display: flex;
    width: 100%;
    min-width: 0;
    align-items: center;
    gap: 11px;
    padding: 10px 12px;
    border: 1px solid rgba(89, 106, 253, 0.1);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.78);
    box-sizing: border-box;
  }

  &__device-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    span {
      flex: none;
      color: #8b919c;
      font-size: 12px;
      line-height: 20px;
    }

    strong {
      min-width: 0;
      overflow: hidden;
      color: #46516b;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
      text-align: right;
      text-overflow: ellipsis;
      white-space: nowrap;
      letter-spacing: 0.3px;
    }
  }

  &__device-icon {
    position: relative;
    display: block;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    border-radius: 10px;
    background: #eef1ff;

    &::before {
      position: absolute;
      top: 7px;
      left: 10px;
      width: 10px;
      height: 15px;
      border: 2px solid #596afd;
      border-radius: 3px;
      content: '';
      box-sizing: border-box;
    }

    &::after {
      position: absolute;
      bottom: 7px;
      left: 14px;
      width: 4px;
      height: 2px;
      border-radius: 1px;
      background: #596afd;
      content: '';
    }
  }

  &__content {
    min-height: 280px;
    box-sizing: border-box;
  }

  &__cards {
    display: grid;
    gap: 12px;
    margin-top: 10px;
  }

  &__card {
    min-width: 0;
    margin: 0;
    padding: 17px 16px 15px;
    border: 1px solid #f0f1f3;
    border-radius: 8px;
    outline: none;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(31, 35, 41, 0.04);
    box-sizing: border-box;
    cursor: pointer;
    transition: background-color 120ms ease, transform 120ms ease;

    &:active {
      background: #fafbfc;
      transform: scale(0.995);
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px rgba(39, 121, 255, 0.22);
    }
  }

  &__card-primary {
    display: grid;
    min-width: 0;
    align-items: center;
    grid-template-columns: minmax(54px, auto) minmax(0, 1fr) auto;
    gap: 10px;

    time {
      color: #858a92;
      font-size: 14px;
      line-height: 22px;
      white-space: nowrap;
    }
  }

  &__name {
    min-width: 0;
    overflow: hidden;
    color: #344263;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__summary {
    display: flex;
    min-width: 0;
    align-items: baseline;
    gap: 4px;
    overflow: hidden;
    color: #8f959e;

    > span:first-child {
      flex: none;
      font-size: 12px;
      line-height: 20px;
    }

    > strong {
      flex: none;
      font-size: 20px;
      font-weight: 700;
      line-height: 28px;
    }

    &.is-good {
      color: #168a4a;
    }

    &.is-fair {
      color: #e77b00;
    }

    &.is-poor {
      color: #e5484d;
    }

    &.is-unknown {
      color: #8f959e;
    }
  }

  &__quality {
    min-width: 0;
    overflow: hidden;
    font-size: 13px;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__card-secondary {
    display: grid;
    min-width: 0;
    margin-top: 11px;
    grid-template-columns: minmax(0, 1fr) minmax(96px, auto);
    gap: 14px;

    span {
      min-width: 0;
      overflow: hidden;
      color: #8a8f98;
      font-size: 13px;
      line-height: 21px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

  }

  &__section-title {
    padding: 0 4px;
    color: #3f4652;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }
}

@media (max-width: 360px) {
  .aroma-report-list {
    padding-right: 10px;
    padding-left: 10px;

    &__card {
      padding: 15px 13px 14px;
    }

    &__card-primary {
      grid-template-columns: minmax(48px, auto) minmax(0, 1fr) auto;
      gap: 7px;

      time {
        font-size: 12px;
      }
    }

    &__name {
      font-size: 16px;
    }

    &__summary {
      > span:first-child {
        display: none;
      }

      > strong {
        font-size: 18px;
      }
    }
  }
}
</style>
